import React from "react";
import axios from "axios";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  MenuItem,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
} from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import EditIcon from "@material-ui/icons/Edit";
import DeleteIcon from "@material-ui/icons/Delete";
import RestoreIcon from "@material-ui/icons/Restore";
import AddIcon from "@material-ui/icons/Add";
import OrderDetailsModal from "../../Components/OrderDetailsModal";
import AddressPinMap from "./AddressPinMap";
import "../AppSettings/styles.css";
import "./styles.css";

const baseUrl = "https://api.appetito.ro/";

// Utilizatori: every customer account. Nothing is listed until a search is
// run (Caută / Enter, not on every key). The search looks through their name,
// phone, email, addresses and orders (mentions, the address/phone written on
// an order) — every word has to match somewhere. Login is by SMS code, so
// there's no password to manage.

const PAGE_SIZE = 50;

const STATUS_LABELS = {
  created: "Plasată",
  accepted: "Acceptată",
  prepared: "Pregătită",
  picked: "Ridicată",
  delivered: "Livrată",
  canceled: "Anulată",
};

const LOCATION_TYPES = [
  {id: "", label: "Nespecificat"},
  {id: "apartament", label: "Apartament"},
  {id: "casa", label: "Casă"},
  {id: "birou", label: "Birou"},
  {id: "hotel", label: "Hotel"},
  {id: "altele", label: "Altele"},
];

const BUILDING_LABELS = {
  apartament: "Bloc",
  birou: "Clădire",
  hotel: "Hotel",
  altele: "Detalii locație",
};

const DROPOFFS = [
  {id: "receptie", label: "La recepție"},
  {id: "fata", label: "În fața hotelului"},
  {id: "camera", label: "În cameră"},
];

const HINT_SOURCES = {user: "Cont", address: "Adresă", order: "Comandă"};

const authHeaders = () => ({headers: {Authorization: `Bearer ${localStorage.getItem("access_token")}`}});

const errorMessage = (error, fallback) => error?.response?.data?.message || fallback;

function pad(n) {
  return `${n}`.padStart(2, "0");
}

function formatDate(value, withTime = true) {
  if (!value) {
    return "—";
  }
  const date = new Date(value);
  if (isNaN(date.getTime()) || date.getFullYear() < 2000) {
    return "—";
  }
  const day = `${pad(date.getDate())}.${pad(date.getMonth() + 1)}.${date.getFullYear()}`;
  return withTime ? `${day} ${pad(date.getHours())}:${pad(date.getMinutes())}` : day;
}

// Same folding as the backend (lowercase, no diacritics); every Romanian
// letter folds to one character, so indexes line up with the original text.
const fold = (text) => text.toLowerCase()
  .replace(/[ăâ]/g, "a").replace(/î/g, "i").replace(/[șş]/g, "s").replace(/[țţ]/g, "t");

const searchWords = (query) => fold(query).split(/[\s,;.:/\\()"'#]+/).filter((w) => w.length >= 2 || /^\d$/.test(w));

// Marks the searched words inside a hint.
function Highlight({text, words}) {
  if (!text || !words.length) {
    return text || null;
  }
  const folded = fold(text);
  const marks = new Array(text.length).fill(false);
  words.forEach((word) => {
    for (let i = folded.indexOf(word); i >= 0; i = folded.indexOf(word, i + 1)) {
      marks.fill(true, i, i + word.length);
    }
  });
  const parts = [];
  let start = 0;
  for (let i = 1; i <= text.length; i++) {
    if (i === text.length || marks[i] !== marks[start]) {
      const piece = text.slice(start, i);
      parts.push(marks[start] ? <mark key={start}>{piece}</mark> : piece);
      start = i;
    }
  }
  return parts;
}

export default class Users extends React.Component {
  state = {
    query: "",
    page: 0,
    users: [],
    total: 0,
    truncated: false,
    loading: false,
    // The query the results are for (the field may have changed since).
    searched: "",
    selectedUserId: null,
  };

  requestId = 0;

  // A link with ?q= repeats that search; ?id= opens the user.
  componentDidMount() {
    const params = new URLSearchParams(this.props.location.search);
    const id = parseInt(params.get("id"), 10);
    const query = (params.get("q") || "").trim();
    this.setState({query, selectedUserId: id || null}, () => {
      if (query) {
        this.search();
      }
    });
  }

  search = () => {
    const searched = this.state.query.trim();
    if (!searched) {
      return;
    }
    this.setState({searched}, () => {
      this.syncUrl();
      this.load(0);
    });
  };

  syncUrl = () => {
    const params = new URLSearchParams();
    if (this.state.searched) {
      params.set("q", this.state.searched);
    }
    if (this.state.selectedUserId) {
      params.set("id", this.state.selectedUserId);
    }
    const search = params.toString();
    this.props.history.replace({search: search ? `?${search}` : ""});
  };

  load = async (page) => {
    const requestId = ++this.requestId;
    this.setState({loading: true});
    try {
      const response = await axios.get(`${baseUrl}v1/admin/users`, {
        ...authHeaders(),
        params: {q: this.state.searched, page},
      });
      if (requestId !== this.requestId) {
        return;
      }
      const {users, total, truncated} = response.data;
      this.setState({users: users || [], total, truncated, page, loading: false});
    } catch (e) {
      if (requestId === this.requestId) {
        this.setState({users: [], total: 0, loading: false});
      }
    }
  };


  openUser = (id) => this.setState({selectedUserId: id}, this.syncUrl);

  closeUser = () => this.setState({selectedUserId: null}, this.syncUrl);

  // A save in the details changes the row (name, phone, counts).
  userChanged = () => this.state.searched && this.load(this.state.page);

  renderHints(user, words) {
    if (!user.hints.length) {
      return null;
    }
    return (
      <div className="users-hints">
        {user.hints.map((hint, i) => (
          <div key={i} className="users-hint">
            <span className={`users-hint-source users-hint-${hint.source}`}>
              {HINT_SOURCES[hint.source]}{hint.source !== "user" ? ` #${hint.ref}` : ""}
            </span>
            <span className="users-hint-field">{hint.field}:</span>
            <span className="users-hint-text"><Highlight text={hint.text} words={words}/></span>
          </div>
        ))}
      </div>
    );
  }

  render() {
    const {query, searched, users, total, page, loading, truncated, selectedUserId} = this.state;
    const words = searchWords(searched);
    const pages = Math.max(1, Math.ceil(total / PAGE_SIZE));
    return (
      <div className="settings-container">
        <div className="settings-header-row">
          <h2 className="settings-title" style={{marginBottom: 0}}>Utilizatori</h2>
          <span className="settings-description">
            {loading ? "Se caută…" : searched ? `${total.toLocaleString("ro-RO")} rezultate` : ""}
          </span>
        </div>

        <div className="settings-section users-section">
          <form
            className="users-search-row"
            onSubmit={(e) => {
              e.preventDefault();
              this.search();
            }}>
            <TextField
              fullWidth
              autoFocus
              variant="outlined"
              value={query}
              onChange={(e) => this.setState({query: e.target.value})}
              placeholder="Caută după nume, telefon, email, adresă, mențiuni din comenzi sau id comandă / utilizator"
            />
            <Button type="submit" variant="contained" color="primary" size="large" disabled={!query.trim() || loading}>
              Caută
            </Button>
          </form>
          <div className="settings-description users-search-help">
            Toate cuvintele trebuie să apară undeva la utilizator: în cont, într-o adresă sau într-o comandă
            (de ex. „razboieni 31”, „0746 115 733”, „orient kebab mall”).
            {truncated && " Cuvintele sunt foarte comune, așa că s-au căutat doar comenzile cele mai recente."}
          </div>

          {!!searched &&
          <Table size="small" className="users-table">
            <TableHead>
              <TableRow>
                <TableCell>ID</TableCell>
                <TableCell>Nume</TableCell>
                <TableCell>Telefon</TableCell>
                <TableCell>Email</TableCell>
                <TableCell align="right">Comenzi</TableCell>
                <TableCell>Ultima comandă</TableCell>
                <TableCell align="right">Adrese</TableCell>
                <TableCell>Înregistrat</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {!loading && users.length === 0 &&
              <TableRow>
                <TableCell colSpan={8} className="settings-description">Niciun utilizator găsit.</TableCell>
              </TableRow>}
              {users.map((user) => (
                <TableRow
                  key={user.id}
                  hover
                  className={`users-row ${user.deleted ? "settings-table-row-deleted" : ""}`}
                  onClick={() => this.openUser(user.id)}>
                  <TableCell>{user.id}</TableCell>
                  <TableCell>
                    <div className="users-name">
                      {`${user.first_name} ${user.last_name}`.trim() || "—"}
                      {user.blacklisted && <span className="users-chip users-chip-black">Listă neagră</span>}
                      {user.deleted && <span className="users-chip users-chip-red">Dezactivat</span>}
                      {user.free_delivery && <span className="users-chip">Livrare gratuită</span>}
                    </div>
                    {this.renderHints(user, words)}
                  </TableCell>
                  <TableCell className="users-nowrap">{user.phone || "—"}</TableCell>
                  <TableCell>{user.email || "—"}</TableCell>
                  <TableCell align="right">{user.orders}</TableCell>
                  <TableCell className="users-nowrap">{formatDate(user.last_order)}</TableCell>
                  <TableCell align="right">{user.addresses}</TableCell>
                  <TableCell className="users-nowrap">{formatDate(user.created, false)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>}

          {pages > 1 &&
          <div className="users-pager">
            <Button disabled={page === 0 || loading} onClick={() => this.load(page - 1)}>Înapoi</Button>
            <span className="settings-description">Pagina {page + 1} din {pages}</span>
            <Button disabled={page + 1 >= pages || loading} onClick={() => this.load(page + 1)}>Înainte</Button>
          </div>}
        </div>

        {!!selectedUserId &&
        <UserDetails userId={selectedUserId} onClose={this.closeUser} onChanged={this.userChanged}/>}
      </div>
    );
  }
}

class UserDetails extends React.Component {
  state = {
    loading: true,
    error: "",
    user: null,
    form: null,
    addresses: [],
    orders: [],
    blacklisted: null,
    saving: false,
    saveError: "",
    saved: false,
    showDeletedAddresses: false,
    editingAddress: null,
    selectedOrder: null,
  };

  componentDidMount() {
    this.load();
  }

  load = async () => {
    try {
      const response = await axios.get(`${baseUrl}v1/admin/users/${this.props.userId}`, authHeaders());
      const {user, addresses, orders, blacklisted} = response.data;
      this.setState({
        loading: false,
        user,
        form: this.formFrom(user),
        addresses: addresses || [],
        orders: orders || [],
        blacklisted,
      });
    } catch (e) {
      this.setState({loading: false, error: errorMessage(e, "Utilizatorul nu a putut fi încărcat.")});
    }
  };

  formFrom = (user) => ({
    first_name: user.first_name || "",
    last_name: user.last_name || "",
    email: user.email || "",
    phone: user.phone || "",
    free_delivery: !!user.free_delivery,
    deleted: !!user.deleted,
  });

  setField = (key, value) => this.setState((state) => ({form: {...state.form, [key]: value}, saveError: "", saved: false}));

  dirty = () => {
    const {form, user} = this.state;
    return !!user && JSON.stringify(form) !== JSON.stringify(this.formFrom(user));
  };

  save = async () => {
    this.setState({saving: true, saveError: "", saved: false});
    try {
      const response = await axios.put(`${baseUrl}v1/admin/users/${this.props.userId}`, this.state.form, authHeaders());
      const user = response.data;
      this.setState({saving: false, saved: true, user, form: this.formFrom(user)});
      this.props.onChanged();
    } catch (e) {
      this.setState({saving: false, saveError: errorMessage(e, "Modificările nu au putut fi salvate.")});
    }
  };

  addressAction = async (request, fallback) => {
    try {
      await request();
      await this.load();
      this.props.onChanged();
    } catch (e) {
      window.alert(errorMessage(e, fallback));
    }
  };

  deleteAddress = (address) => {
    if (!window.confirm(`Ștergi adresa „${address.formatted}”? Comenzile vechi o păstrează.`)) {
      return;
    }
    this.addressAction(
      () => axios.delete(`${baseUrl}v1/admin/users/${this.props.userId}/addresses/${address.id}`, authHeaders()),
      "Adresa nu a putut fi ștearsă.",
    );
  };

  restoreAddress = (address) => this.addressAction(
    () => axios.post(`${baseUrl}v1/admin/users/${this.props.userId}/addresses/${address.id}/restore`, {}, authHeaders()),
    "Adresa nu a putut fi restaurată.",
  );

  addressSaved = async () => {
    this.setState({editingAddress: null});
    await this.load();
    this.props.onChanged();
  };

  openOrder = async (orderId) => {
    try {
      const response = await axios.get(`${baseUrl}v1/admin/order/${orderId}`, authHeaders());
      this.setState({selectedOrder: response.data});
    } catch (e) {
      window.alert("Comanda nu a putut fi încărcată.");
    }
  };

  renderForm() {
    const {form, user, saving, saveError, saved, blacklisted} = this.state;
    const phoneChanged = form.phone.trim() !== (user.phone || "");
    // 'admin' in localStorage is the super-admin flag (see OrderDetailsModal).
    const isSuperAdmin = localStorage.getItem("admin") === "true";
    return (
      <div className="settings-section users-detail-section">
        <div className="settings-section-header">
          <h3 className="settings-section-title">Date cont</h3>
          <span className="settings-description">
            Înregistrat {formatDate(user.created)}
            {user.referral_code ? ` · cod referral ${user.referral_code}` : ""}
          </span>
        </div>
        {blacklisted &&
        <div className="users-warning">
          Numărul {user.phone} este în lista neagră{blacklisted.details ? ` (${blacklisted.details})` : ""}: fără comenzi cash, iar
          numărul nu poate fi schimbat.
        </div>}
        <div className="users-form-grid">
          <TextField label="Prenume" value={form.first_name} inputProps={{maxLength: 20}}
                     onChange={(e) => this.setField("first_name", e.target.value)}/>
          <TextField label="Nume" value={form.last_name} inputProps={{maxLength: 20}}
                     onChange={(e) => this.setField("last_name", e.target.value)}/>
          <TextField label="Telefon (login cu cod SMS)" value={form.phone}
                     helperText={phoneChanged ? "Nu poate fi folosit de alt cont activ și nici numărul actual sau cel nou nu pot fi în lista neagră." : " "}
                     onChange={(e) => this.setField("phone", e.target.value)}/>
          <TextField label="Email" value={form.email} inputProps={{maxLength: 50}}
                     onChange={(e) => this.setField("email", e.target.value)}/>
        </div>
        <div className="users-switches">
          <FormControlLabel
            title={isSuperAdmin ? "" : "Doar un super admin poate modifica livrarea gratuită"}
            control={<Switch color="primary" checked={form.free_delivery} disabled={!isSuperAdmin}
                             onChange={(e) => this.setField("free_delivery", e.target.checked)}/>}
            label={isSuperAdmin ? "Livrare gratuită" : "Livrare gratuită (doar super admin)"}/>
          <FormControlLabel
            control={<Switch checked={form.deleted} onChange={(e) => this.setField("deleted", e.target.checked)}/>}
            label="Cont dezactivat"/>
        </div>
        {!!saveError && <div className="users-error">{saveError}</div>}
        <div className="users-form-actions">
          {saved && !this.dirty() && <span className="users-saved">Salvat</span>}
          <Button disabled={!this.dirty() || saving} onClick={() => this.setState({form: this.formFrom(user), saveError: ""})}>
            Renunță
          </Button>
          <Button variant="contained" color="primary" disabled={!this.dirty() || saving} onClick={this.save}>
            {saving ? "Se salvează…" : "Salvează"}
          </Button>
        </div>
      </div>
    );
  }

  renderAddresses() {
    const {addresses, showDeletedAddresses} = this.state;
    const deletedCount = addresses.filter((a) => a.deleted).length;
    const shown = addresses.filter((a) => showDeletedAddresses || !a.deleted);
    return (
      <div className="settings-section users-detail-section">
        <div className="settings-section-header">
          <h3 className="settings-section-title">Adrese</h3>
          <div className="users-header-actions">
            {deletedCount > 0 &&
            <FormControlLabel
              control={<Switch size="small" checked={showDeletedAddresses}
                               onChange={(e) => this.setState({showDeletedAddresses: e.target.checked})}/>}
              label={`Șterse (${deletedCount})`}/>}
            <Button size="small" color="primary" startIcon={<AddIcon/>} onClick={() => this.setState({editingAddress: {}})}>
              Adaugă
            </Button>
          </div>
        </div>
        {shown.length === 0 && <div className="settings-description users-empty">Nicio adresă.</div>}
        {shown.map((address) => (
          <div key={address.id} className={`users-address ${address.deleted ? "settings-table-row-deleted" : ""}`}>
            <div className="users-address-info">
              <div className="users-address-line">
                {address.formatted}
                {address.deleted && <span className="users-chip users-chip-red">Ștearsă</span>}
                {!!address.location_type &&
                <span className="users-chip">{(LOCATION_TYPES.find((t) => t.id === address.location_type) || {}).label}</span>}
              </div>
              {!!address.details && <div className="settings-description">Indicații: {address.details}</div>}
              {!!address.description && <div className="settings-description">Descriere: {address.description}</div>}
              <div className="settings-description">
                #{address.id} · adăugată {formatDate(address.created, false)} · {address.orders} comenzi
              </div>
            </div>
            <div className="users-address-actions">
              {address.deleted ? (
                <IconButton size="small" title="Restaurează" onClick={() => this.restoreAddress(address)}><RestoreIcon/></IconButton>
              ) : (
                <>
                  <IconButton size="small" title="Editează" onClick={() => this.setState({editingAddress: address})}><EditIcon/></IconButton>
                  <IconButton size="small" title="Șterge" onClick={() => this.deleteAddress(address)}><DeleteIcon/></IconButton>
                </>
              )}
            </div>
          </div>
        ))}
      </div>
    );
  }

  renderOrders() {
    const {orders} = this.state;
    return (
      <div className="settings-section users-detail-section">
        <div className="settings-section-header">
          <h3 className="settings-section-title">Comenzi</h3>
          <span className="settings-description">
            {orders.length >= 500 ? "Ultimele 500" : `${orders.length} comenzi`}
          </span>
        </div>
        {orders.length === 0 ? <div className="settings-description users-empty">Nicio comandă.</div> : (
          <Table size="small" className="users-table">
            <TableHead>
              <TableRow>
                <TableCell>ID</TableCell>
                <TableCell>Data</TableCell>
                <TableCell>Restaurant</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Adresă</TableCell>
                <TableCell>Mențiuni</TableCell>
                <TableCell>Plată</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {orders.map((order) => (
                <TableRow key={order.id} hover className={`users-row ${order.deleted ? "settings-table-row-deleted" : ""}`}
                          onClick={() => this.openOrder(order.id)}>
                  <TableCell>{order.id}</TableCell>
                  <TableCell className="users-nowrap">{formatDate(order.created)}</TableCell>
                  <TableCell>{order.restaurant}{order.restaurant_city ? `, ${order.restaurant_city}` : ""}</TableCell>
                  <TableCell>
                    <span className={`users-status users-status-${order.status}`}>{STATUS_LABELS[order.status] || order.status}</span>
                  </TableCell>
                  <TableCell>
                    {!order.delivery ? "Ridicare personală" : (order.address || "—")}
                    {!!order.address_string && <div className="settings-description">Scris pe comandă: {order.address_string}</div>}
                    {!!order.phone && <div className="settings-description">Tel. comandă: {order.phone}</div>}
                  </TableCell>
                  <TableCell>{order.mentions || ""}</TableCell>
                  <TableCell className="users-nowrap">{order.paid ? "Card" : "Cash"}{order.source ? ` · ${order.source}` : ""}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>
    );
  }

  render() {
    const {loading, error, user, editingAddress, selectedOrder} = this.state;
    const name = user ? `${user.first_name} ${user.last_name}`.trim() : "";
    return (
      <Dialog open onClose={this.props.onClose} fullWidth maxWidth="lg" scroll="paper">
        <DialogTitle disableTypography className="users-dialog-title">
          <span className="users-name">
            {user ? `${name || "Fără nume"} · #${user.id}` : "Utilizator"}
            {this.state.blacklisted && <span className="users-chip users-chip-black">Listă neagră</span>}
          </span>
          <IconButton onClick={this.props.onClose}><CloseIcon/></IconButton>
        </DialogTitle>
        <DialogContent dividers className="users-dialog-content settings-container">
          {loading && <div className="loader-container"><div className="loader"/></div>}
          {!!error && <div className="users-error">{error}</div>}
          {user && this.renderForm()}
          {user && this.renderAddresses()}
          {user && this.renderOrders()}
        </DialogContent>
        {editingAddress &&
        <AddressDialog
          userId={this.props.userId}
          address={editingAddress}
          onClose={() => this.setState({editingAddress: null})}
          onSaved={this.addressSaved}/>}
        <OrderDetailsModal order={selectedOrder} onClose={() => this.setState({selectedOrder: null})}/>
      </Dialog>
    );
  }
}

const ADDRESS_FIELDS = ["location_type", "city", "street", "number", "building", "entrance", "floor", "apartment", "dropoff",
  "details", "description", "latitude", "longitude"];

// Add / edit an address. An edit is saved as a new address (the old one is
// soft-deleted) so past orders keep the address they went to.
class AddressDialog extends React.Component {
  constructor(props) {
    super(props);
    const form = {};
    ADDRESS_FIELDS.forEach((key) => {
      form[key] = props.address[key] ?? "";
    });
    if (!props.address.id && !form.city) {
      form.city = "Turda";
    }
    this.state = {form, saving: false, error: ""};
  }

  setField = (key, value) => this.setState((state) => ({form: {...state.form, [key]: value}, error: ""}));

  save = async () => {
    const {form} = this.state;
    const {userId, address} = this.props;
    const body = {...form, latitude: Number(form.latitude) || 0, longitude: Number(form.longitude) || 0};
    this.setState({saving: true, error: ""});
    try {
      if (address.id) {
        await axios.put(`${baseUrl}v1/admin/users/${userId}/addresses/${address.id}`, body, authHeaders());
      } else {
        await axios.post(`${baseUrl}v1/admin/users/${userId}/addresses`, body, authHeaders());
      }
      this.props.onSaved();
    } catch (e) {
      this.setState({saving: false, error: errorMessage(e, "Adresa nu a putut fi salvată.")});
    }
  };

  render() {
    const {form, saving, error} = this.state;
    const type = form.location_type;
    const field = (key, label, props = {}) => (
      <TextField label={label} value={form[key]} onChange={(e) => this.setField(key, e.target.value)} {...props}/>
    );
    return (
      <Dialog open onClose={this.props.onClose} fullWidth maxWidth="md">
        <DialogTitle>{this.props.address.id ? "Editează adresa" : "Adresă nouă"}</DialogTitle>
        <DialogContent>
          {!!this.props.address.id &&
          <div className="settings-description users-address-note">
            Se salvează ca adresă nouă, iar cea veche se șterge: comenzile vechi rămân cu adresa la care au fost livrate.
          </div>}
          <div className="users-form-grid">
            <TextField select label="Tip locație" value={type} InputLabelProps={{shrink: true}} SelectProps={{displayEmpty: true}} onChange={(e) => this.setField("location_type", e.target.value)}>
              {LOCATION_TYPES.map((t) => <MenuItem key={t.id} value={t.id}>{t.label}</MenuItem>)}
            </TextField>
            {field("city", "Oraș")}
            {field("street", "Stradă")}
            {field("number", "Număr")}
            {(BUILDING_LABELS[type] || type === "") && field("building", BUILDING_LABELS[type] || "Bloc / clădire")}
            {(type === "apartament" || type === "") && field("entrance", "Scara")}
            {type !== "casa" && type !== "hotel" && field("floor", "Etaj")}
            {type === "hotel" ? (
              <TextField select label="Predare" value={form.dropoff} onChange={(e) => this.setField("dropoff", e.target.value)}>
                {DROPOFFS.map((d) => <MenuItem key={d.id} value={d.id}>{d.label}</MenuItem>)}
              </TextField>
            ) : type !== "casa" && field("apartment", type === "birou" ? "Birou / interior" : "Apartament")}
            {type === "hotel" && form.dropoff === "camera" && field("apartment", "Camera")}
          </div>
          <div className="users-form-grid users-form-grid-wide">
            {field("details", "Indicații pentru curier", {multiline: true})}
            {field("description", "Descriere", {multiline: true})}
          </div>
          <AddressPinMap
            latitude={form.latitude}
            longitude={form.longitude}
            query={() => `${form.street} ${form.number}, ${form.city}, România`}
            onChange={(latitude, longitude) => this.setState((state) => ({form: {...state.form, latitude, longitude}, error: ""}))}/>
          {!!error && <div className="users-error">{error}</div>}
        </DialogContent>
        <DialogActions>
          <Button onClick={this.props.onClose} disabled={saving}>Renunță</Button>
          <Button variant="contained" color="primary" onClick={this.save} disabled={saving}>
            {saving ? "Se salvează…" : "Salvează"}
          </Button>
        </DialogActions>
      </Dialog>
    );
  }
}
